/**
 * Select Primitive
 * Dropdown select component with validation states and design system tokens
 */

import React from "react";
import {
  Select as ChakraSelect,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
} from "@chakra-ui/react";
import {
  resolveSpacing,
} from "../foundation";
import { Color } from "../foundation/Color";
import { Spacing } from "../foundation/Spacing";
import { Radius } from "../foundation/Radius";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for select tokens
declare const __select_size_brand: unique symbol;
declare const __select_state_brand: unique symbol;

export interface SelectSizeToken {
  readonly [__select_size_brand]: true;
  readonly _value: string;
}

export interface SelectStateToken {
  readonly [__select_state_brand]: true;
  readonly _value: string;
}

// Factory functions
const createSelectSizeToken = (value: string): SelectSizeToken => ({ _value: value } as SelectSizeToken);
const createSelectStateToken = (value: string): SelectStateToken => ({ _value: value } as SelectStateToken);

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  description?: string;
}

export interface SelectProps {
  options: SelectOption[];
  label?: string;
  helperText?: string;
  errorText?: string;
  placeholder?: string;
  size?: SelectSizeToken;
  state?: SelectStateToken;
  fullWidth?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  identifier?: string;
  id?: string;
  "data-testid"?: string;
}

function SelectComponent({
  options,
  label,
  helperText,
  errorText,
  placeholder,
  size,
  state,
  fullWidth = false,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  required = false,
  multiple = false,
  identifier,
  id,
  "data-testid": testId,
}: SelectProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedSize = size || selectTokens.size.medium;
  const resolvedState = state || selectTokens.state.default;

  // Determine validation state
  const isInvalid = resolvedState._value === "error" || !!errorText;
  const isSuccess = resolvedState._value === "success";
  const isWarning = resolvedState._value === "warning";

  // Get state colors
  const getStateColors = () => {
    if (isInvalid) {
      return {
        borderColor: useColorModeValue(Color.primitive.red(500)),
        focusBorderColor: useColorModeValue(Color.primitive.red(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.red(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.red(500))}`,
        },
      };
    } else if (isSuccess) {
      return {
        borderColor: useColorModeValue(Color.primitive.green(500)),
        focusBorderColor: useColorModeValue(Color.primitive.green(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.green(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.green(500))}`,
        },
      };
    } else if (isWarning) {
      return {
        borderColor: useColorModeValue(Color.primitive.orange(500)),
        focusBorderColor: useColorModeValue(Color.primitive.orange(500)),
        _focus: {
          borderColor: useColorModeValue(Color.primitive.orange(500)),
          boxShadow: `0 0 0 1px ${useColorModeValue(Color.primitive.orange(500))}`,
        },
      };
    }

    return {
      borderColor: useColorModeValue(Color.border.primary),
      focusBorderColor: useColorModeValue(Color.interactive.primary),
      _focus: {
        borderColor: useColorModeValue(Color.interactive.primary),
        boxShadow: `0 0 0 1px ${useColorModeValue(Color.interactive.primary)}`,
      },
    };
  };

  const getSizeProps = (size: SelectSizeToken) => {
    switch (size._value) {
      case "small":
        return {
          size: "sm" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
      case "large":
        return {
          size: "lg" as const,
          fontSize: "md",
          px: resolveSpacing(Spacing.medium),
          py: resolveSpacing(Spacing.small),
        };
      case "medium":
      default:
        return {
          size: "md" as const,
          fontSize: "sm",
          px: resolveSpacing(Spacing.small),
          py: resolveSpacing(Spacing.tiny),
        };
    }
  };

  const stateColors = getStateColors();
  const sizeProps = getSizeProps(resolvedSize);

  return (
    <FormControl isInvalid={isInvalid} isRequired={required} width={fullWidth ? "full" : "auto"}>
      {label && (
        <FormLabel
          htmlFor={id}
          fontSize={sizeProps.fontSize}
          color={useColorModeValue(Color.text.primary)}
          mb={resolveSpacing(Spacing.tiny)}
        >
          {label}
        </FormLabel>
      )}

      <ChakraSelect
        {...sizeProps}
        {...stateColors}
        bg={useColorModeValue(Color.surface.input)}
        color={useColorModeValue(Color.text.primary)}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        isDisabled={disabled}
        isRequired={required}
        multiple={multiple}
        name={identifier}
        id={id}
        data-testid={testId}
        borderRadius={Radius.input._value}
        width={fullWidth ? "full" : "auto"}
        _disabled={{
          opacity: 0.6,
          cursor: "not-allowed",
          bg: useColorModeValue(Color.surface.secondary),
        }}
        placeholder={placeholder}
      >
        {placeholder && !value && !defaultValue && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </ChakraSelect>

      {errorText && (
        <FormErrorMessage
          fontSize="xs"
          color={useColorModeValue(Color.text.error)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {errorText}
        </FormErrorMessage>
      )}

      {helperText && !errorText && (
        <FormHelperText
          fontSize="xs"
          color={useColorModeValue(Color.text.secondary)}
          mt={resolveSpacing(Spacing.tiny)}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

// Select tokens
const selectTokens = {
  size: {
    small: createSelectSizeToken("small"),
    medium: createSelectSizeToken("medium"),
    large: createSelectSizeToken("large"),
  } as const,

  state: {
    default: createSelectStateToken("default"),
    success: createSelectStateToken("success"),
    warning: createSelectStateToken("warning"),
    error: createSelectStateToken("error"),
  } as const,
};

// Public API - combines tokens and component
export const Select = Object.assign(SelectComponent, selectTokens);
